import { registerNavContribution } from './navContributions'
import type { NavItem } from './types'

import { alertNavItems } from '@/features/alerts'
import { anomalyNavItems } from '@/features/anomalies'
import { audienceNavItems } from '@/features/audience'
import { orphexMcpNavItems } from '@/features/auth'
import { commerceNavItems } from '@/features/commerce-analytics'
import { creativeAiNavItems } from '@/features/creative-ai'
import { createDashboardNavItemsProvider } from '@/features/dashboards'
import { dataExportNavItems } from '@/features/data-export'
import { createGetStartedNavItemsProvider } from '@/features/global-surface'
import { healthCheckNavItems } from '@/features/health-checks'
import { impactNavItems } from '@/features/impact-analysis'
import { insightNavItems } from '@/features/insights'
import { marketingInsightsNavItems } from '@/features/marketing-insights'
import { orpheusNavItems } from '@/features/orpheus'
import { platformActionNavItems } from '@/features/platform-actions'
import { reportNavItems } from '@/features/reports'
import { scorecardNavItems } from '@/features/scorecards'
import { workflowNavItems } from '@/features/workflow'
import { workspaceSettingsNavItems } from '@/features/workspace-settings'

let registered = false

/** Compose only route-ready module-27 leaves into module-08's existing groups. */
function reportItemsWithVerticalLeaves(): NavItem[] {
  const benchmark = commerceNavItems.find((item) => item.navKey === 'benchmark') as
    NavItem | undefined
  const performanceOverview = commerceNavItems.find(
    (item) => item.navKey === 'performanceOverview',
  ) as NavItem | undefined
  const customerIntelligence = commerceNavItems.find(
    (item) => item.navKey === 'customerIntelligence',
  ) as NavItem | undefined
  const productInsights = commerceNavItems.find((item) => item.navKey === 'productInsights') as
    NavItem | undefined
  const funnelExploration = audienceNavItems.find((item) => item.navKey === 'funnelExploration') as
    NavItem | undefined
  const reportItems = (reportNavItems as NavItem[]).map((item) => {
    if (item.navKey === 'creativePerformanceGroup') {
      const [creativePerformance, ...remaining] = item.children ?? []
      return {
        ...item,
        children: [
          creativePerformance,
          ...(creativeAiNavItems as NavItem[]),
          ...remaining,
          benchmark,
        ].filter((leaf): leaf is NavItem => leaf !== undefined),
      }
    }
    if (item.navKey === 'budgetPerformanceGroup') {
      const appAcquisition = item.children?.find((child) => child.navKey === 'appAcquisition')
      const core = item.children?.filter((child) => child.navKey !== 'appAcquisition') ?? []
      return {
        ...item,
        children: [...core, performanceOverview, appAcquisition].filter(
          (leaf): leaf is NavItem => leaf !== undefined,
        ),
      }
    }
    if (item.navKey === 'ecommerceGroup') {
      return {
        ...item,
        children: [...(item.children ?? []), customerIntelligence, productInsights].filter(
          (leaf): leaf is NavItem => leaf !== undefined,
        ),
      }
    }
    if (item.navKey === 'audienceGroup' && funnelExploration) {
      const [demographics, ...remaining] = item.children ?? []
      return {
        ...item,
        children: [demographics, funnelExploration, ...remaining].filter(
          (leaf): leaf is NavItem => leaf !== undefined,
        ),
      }
    }
    return item
  })
  const audienceIndex = reportItems.findIndex((item) => item.navKey === 'audienceGroup')
  if (audienceIndex < 0) return [...reportItems, ...(marketingInsightsNavItems as NavItem[])]
  return [
    ...reportItems.slice(0, audienceIndex),
    ...(marketingInsightsNavItems as NavItem[]),
    ...reportItems.slice(audienceIndex),
  ]
}

/** Placeholder Summary contribution from contract 05 §17; module 28 replaces this item. */
export function registerShellNavigation(): void {
  if (registered) return
  const getStartedNavItems = createGetStartedNavItemsProvider()
  const dashboardNavItems = createDashboardNavItemsProvider()
  const dashboardOnlyNavItems = createDashboardNavItemsProvider({
    includeCreate: false,
    includeAccountCenters: false,
  })
  registerNavContribution({
    context: 'app',
    order: 0,
    items: () => [
      ...getStartedNavItems(),
      {
        labelKey: 'shell.nav.summary',
        icon: 'bolt',
        navKey: 'summary',
        to: { name: 'Summary' },
      },
      // ORP-27467 — directly under Summary, in the paid and the Free menu alike.
      ...orphexMcpNavItems,
      ...orpheusNavItems,
      {
        labelKey: 'shell.nav.cockpit',
        icon: 'magnifying-glass-chart',
        navKey: 'cockpit',
        children: [
          // Insights leads this group in v3's Cockpit block (ORP-20390). The existing leaves keep
          // their current relative order — only the missing leaf is added.
          ...insightNavItems,
          ...alertNavItems,
          ...anomalyNavItems,
          ...healthCheckNavItems,
        ],
      },
      {
        // v3's `Workflows` group (`DefaultLayout.vue:228–257`), which sits between Cockpit and
        // Creative Analytics. Module 14 contributes AI Workflow, module 09 contributes KPI
        // Tracking, while module 13 contributes Impact Analysis. The group carries no visibility
        // rule: each leaf owns its server-authored gate and `buildNavNode` drops an empty group.
        // v3's circle-nodes silhouette uses the typed local icon registry.
        labelKey: 'shell.nav.workflows',
        icon: 'circle-nodes',
        navKey: 'workflows',
        children: [...workflowNavItems, ...impactNavItems, ...scorecardNavItems],
      },
    ],
  })
  // Module 08's report groups sit after the shell block (v3 sidebar order). Route-ready vertical
  // leaves are composed into those groups by `reportItemsWithVerticalLeaves`.
  registerNavContribution({
    context: 'app',
    order: 40,
    items: dashboardNavItems,
  })
  registerNavContribution({
    context: 'dashboardsOnly',
    order: 0,
    items: dashboardOnlyNavItems,
  })
  registerNavContribution({
    context: 'app',
    order: 10,
    items: reportItemsWithVerticalLeaves,
  })
  // New Front's trailing standalone leaves: Data Export followed by Approvals, before the
  // settings utility. Approvals used to sit under Cockpit; its current source-of-
  // truth placement is top-level (`DefaultLayout.vue`, after Data Export).
  registerNavContribution({
    context: 'app',
    order: 50,
    items: () => [...dataExportNavItems, ...platformActionNavItems],
  })
  registerNavContribution({
    context: 'app',
    order: 100,
    items: () => [...workspaceSettingsNavItems],
  })
  registered = true
}
